import { useQuery } from '@tanstack/react-query';
import PaymentsAPIServiceV2 from './PaymentsServiceV2';
import { EmployeeDetailRequest } from './ReportsInternalTypes';

const paymentsService = PaymentsAPIServiceV2.getInstance();

export const REPORTS_QUERY_KEYS = {
    GET_EMPLOYEE_DETAILS: 'GET_EMPLOYEE_DETAILS',
    GET_EMPLOYEE_SICKNESS: 'GET_EMPLOYEE_SICKNESS',
    GET_DEPARTMENTS: 'GET_DEPARTMENTS',
    GET_USER_COMMUNICATION_HISTORY: 'GET_USER_COMMUNICATION_HISTORY',
    GET_CAMPAIGN_ACTIVATION_FUNNEL: 'GET_CAMPAIGN_ACTIVATION_FUNNEL'
};

/**
 * Per-user communication & activity history report (US-3250). SuperAdmin-only on the
 * backend (403 otherwise). companyId + userId are both required so a user can never be
 * pulled outside their company.
 */
export const useUserCommunicationHistoryQuery = (
    companyId: string,
    userId: string,
    enabled: boolean
) =>
    useQuery({
        queryKey: [
            REPORTS_QUERY_KEYS.GET_USER_COMMUNICATION_HISTORY,
            companyId,
            userId
        ],
        queryFn: () =>
            paymentsService
                .getUserCommunicationHistory(companyId, userId)
                .then((res) => res.data),
        enabled,
        retry: false
    });

/**
 * Aggregate per-campaign activation funnel report (US-3251). SuperAdmin-only on the
 * backend (401/403 otherwise). companyId rides in the header; campaignId + the
 * adjustable attributionWindowDays are query params. Disabled until the operator
 * submits via Run, so typing never refires the request.
 */
export const useCampaignActivationFunnelQuery = (
    companyId: string,
    campaignId: string,
    attributionWindowDays: number,
    enabled: boolean
) =>
    useQuery({
        queryKey: [
            REPORTS_QUERY_KEYS.GET_CAMPAIGN_ACTIVATION_FUNNEL,
            companyId,
            campaignId,
            attributionWindowDays
        ],
        queryFn: () =>
            paymentsService
                .getCampaignActivationFunnel(
                    companyId,
                    campaignId,
                    attributionWindowDays
                )
                .then((res) => res.data),
        enabled,
        retry: false
    });

export const useReportsQuery = ({
    enableGetEmployeeSicknessQuery = false,
    enableGetDepartmentsQuery = false,
    employeeDetailParams = undefined
}: {
    enableGetEmployeeDetailsQuery?: boolean;
    enableGetEmployeeSicknessQuery?: boolean;
    enableGetDepartmentsQuery?: boolean;
    employeeDetailParams?: EmployeeDetailRequest;
}) => {
    const getEmployeeSicknessQuery = useQuery({
        queryKey: [
            REPORTS_QUERY_KEYS.GET_EMPLOYEE_SICKNESS,
            employeeDetailParams
        ],
        queryFn: () =>
            paymentsService.getEmployeeSickness(
                employeeDetailParams!.departments
            ),
        enabled: enableGetEmployeeSicknessQuery,
        retry: false
    });

    const getDepartmentsQuery = useQuery({
        queryKey: [REPORTS_QUERY_KEYS.GET_DEPARTMENTS],
        queryFn: () => paymentsService.getDepartments(),
        enabled: enableGetDepartmentsQuery,
        retry: false
    });

    return {
        getEmployeeSicknessQuery,
        getDepartmentsQuery
    };
};

export const useEmployeeDetailsQuery = (
    enabled: boolean,
    employeeDetailParams: EmployeeDetailRequest
) =>
    useQuery({
        queryKey: [
            REPORTS_QUERY_KEYS.GET_EMPLOYEE_DETAILS,
            employeeDetailParams.departments,
            employeeDetailParams.startDate,
            employeeDetailParams.endDate
        ],
        queryFn: () =>
            paymentsService.getEmployeeDetails(employeeDetailParams!),
        enabled: enabled,
        retry: false
    });
